import {isFeatureEnabled} from 'feature-flags';
import {
  DropdownActionMenuItem,
  openConfirmDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';

import {useRef} from 'react';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useConvertForAllVehiclesMutation,
  useDeleteInterestMutation,
  useOpenInterestMutation,
  useReopenInterestMutation,
} from '@dms/api/finance';
import {useGetVehicleQuery} from '@dms/api/sales';
import {ApiError, EntityResourceIds} from '@dms/api/shared';
import {ConditionEnum} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {businessCaseRoutes, interestRoutes, testIds} from '@dms/routes';
import {
  EntityHeaderAction,
  InterestEndCaseDialog,
  getFieldAvailabilityContextFromVehicle,
  handleApiError,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  useMandatoryFieldsErrorDialog,
  usePermissions,
  useMandatoryFields,
  useInterestEditPermissions,
} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {CloneVehiclesConfirmDialog} from '../components/CloneVehiclesConfirmDialog';
import {canCreateBusinessCase} from '../utils/canCreateBusinessCase';
import {getLastActiveBusinessCase} from '../utils/getLastActiveBusinessCase';
import {isCreateBusinessCaseDisabled} from '../utils/isCreateBusinessCaseDisabled';
import {useInterestData} from './useInterestData';

export function useInterestActions(interestId: string) {
  const navigate = useNavigate();
  const isCloneFlowEnabled = isFeatureEnabled(featureFlags.INTEREST_CLONE_FLOW);

  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {interest, miniSale, offeredVehiclesList, isActionsDisabled, miniPurchase, customer} =
    useInterestData(interestId);

  const clonedVehicleMapRef = useRef(new Map<string, string>());

  const [deleteInterest] = useDeleteInterestMutation();
  const [convertForAllVehicles, {isLoading: isConvertAllVehiclesLoading}] =
    useConvertForAllVehiclesMutation();
  const [openInterest, {isLoading: isOpenInterestLoading}] = useOpenInterestMutation();
  const [reopenInterest, {isLoading: isReopenInterestLoading}] = useReopenInterestMutation();

  const {data: interestParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
  });

  const [
    hasDeleteInterestPermission,
    hasCreateBusinessCasePurchasePermission,
    hasCreateBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    hasAddVehicleToSellPermission,
    canViewBusinessCaseSelling,
    canViewBusinessCasePurchase,
  ] = usePermissions({
    permissionKeys: [
      'deleteInterest',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'addVehicleToSell',
      'viewBusinessCaseSelling',
      'viewBusinessCasePurchase',
    ],
    scopes: {
      deleteInterest: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
      viewBusinessCaseSelling: {
        // No need for participation scope. This ACL is used for generic creation of BC
        participation: 'SKIP',
        // No need for participation scope. This ACL is used for generic creation of BC
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // No need for participation scope. This ACL is used for generic creation of BC
        participation: 'SKIP',
        // No need for participation scope. This ACL is used for generic creation of BC
        branchId: 'SKIP',
      },
    },
  });

  const {
    hasOpenInterestPermission,
    hasEndInterestPermission,
    hasConvertToBusinessCaseInterestPermission,
    hasReopenInterestPermission,
  } = useInterestEditPermissions({
    interestId,
  });

  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();

  const vehicleId = miniPurchase?.offeredVehiclesIds?.[0];
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {handleMandatoryFieldsError, isMandatoryFieldsError} = useMandatoryFieldsErrorDialog(
    vehicleId,
    {interestId, isMiniSale: isNotNilOrEmpty(miniSale)}
  );

  const canCreatePurchase = hasCreateBusinessCasePurchasePermission && hasAddVehicleToBuyPermission;
  const canCreateSell = hasCreateBusinessCaseSellingPermission && hasAddVehicleToSellPermission;

  const onConvertToBusinessCase = () => {
    convertForAllVehicles({
      interestId,
      validateMandatoryFields: isMandatoryFieldsEnabled(
        (vehicleData?.vehicle?.condition as ConditionEnum) ?? undefined,
        getFieldAvailabilityContextFromVehicle(vehicleData)
      ),
    })
      .unwrap()
      .then((response) => {
        const lastActiveBusinessCase = getLastActiveBusinessCase(response.activeBusinessCases);

        if (!lastActiveBusinessCase) {
          showNotification.error();
          return;
        }

        navigate(
          composePath(businessCaseRoutes.overview, {
            params: {id: lastActiveBusinessCase.businessCaseId},
          })
        );
      })
      .catch((error: ApiError) => {
        if (isMandatoryFieldsError(error)) {
          return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
        }

        return handleApiError(error);
      });
    handleSearchAuthorizationProfiles(
      {
        vehicleMake: vehicleData?.make ?? miniSale?.vehicleData?.make ?? '',
        vehicleType: vehicleData?.type ?? miniSale?.vehicleData?.type ?? null,
        vehicleModelFamily:
          vehicleData?.vehicle?.modelFamily ?? miniSale?.vehicleData?.modelFamily ?? null,
        vehicleModelFamilyGroup: vehicleData?.vehicle?.modelFamilyGroup ?? null,
      },
      interest?.customerId ?? undefined
    );
  };

  const handleCreateBusinessCase = isCloneFlowEnabled
    ? () =>
        openDialog(
          <CloneVehiclesConfirmDialog
            data-testid={testIds.interest.detail('cloneVehiclesConfirmDialog')}
            interestId={interestId}
            clonedVehicleMapRef={clonedVehicleMapRef}
          />,
          {
            'data-testid': testIds.interest.detail('cloneVehiclesConfirmDialog'),
            title: i18n.t('entity.interest.labels.cloneVehiclesBeforeBusinessCase'),
          }
        )
    : onConvertToBusinessCase;

  const handleReopen = () =>
    openConfirmDialog({
      text: i18n.t('entity.interest.labels.confirmReopen'),
      'data-testid': testIds.interest.detail('reopen-interest'),
      onConfirm: () =>
        reopenInterest({
          id: interestId,
        })
          .unwrap()
          .catch(handleApiError),
    });

  const dropdownIconButtonMenuItems = buildArray<DropdownActionMenuItem>()
    .when(interest?.state === 'OPEN', {
      label: i18n.t('general.actions.end'),
      isDisabled: !hasEndInterestPermission,
      onClick: () =>
        openDialog(
          <InterestEndCaseDialog
            data-testid={testIds.interest.detail('submenu-action-endCaseDialog')}
            interestId={interestId}
          />,
          {
            'data-testid': testIds.interest.detail('submenu-action-endCaseDialog'),
            title: i18n.t('entity.interest.labels.endInterestCase'),
            scrollBehavior: 'outside',
          }
        ),
    })
    .when(hasDeleteInterestPermission, {
      label: i18n.t('entity.interest.actions.delete'),
      onClick: () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteInterest({
              id: interestId,
            })
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('entity.interest.notifications.deleteSuccess'));
                navigate(interestRoutes.overview);
              })
              .catch(handleApiError),
        }),
    });

  return buildArray<EntityHeaderAction>()
    .when(interest?.state === 'NEW' && dropdownIconButtonMenuItems.length > 0, {
      type: 'dropdown-iconButton',
      priority: 'secondary',
      menuItems: dropdownIconButtonMenuItems,
      'data-testid': testIds.interest.detail('headerActions-submenu'),
    })
    .when(interest?.state === 'OPEN', {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.end'),
      'data-testid': testIds.interest.detail('headerActions-end'),
      isDisabled: !hasEndInterestPermission,
      onClick: () =>
        openDialog(
          <InterestEndCaseDialog
            data-testid={testIds.interest.detail('action-endCaseDialog')}
            interestId={interestId}
          />,
          {
            'data-testid': testIds.interest.detail('action-endCaseDialog'),
            title: i18n.t('entity.interest.labels.endInterestCase'),
            scrollBehavior: 'outside',
          }
        ),
    })
    .when(
      canCreateBusinessCase({
        state: interest?.state,
        type: interest?.type,
        canCreateSellingBusinessCase: canCreateSell && canViewBusinessCaseSelling,
        canCreateBuyingBusinessCase: canCreatePurchase && canViewBusinessCasePurchase,
      }),
      {
        type: 'button',
        variant: interest?.state === 'OPEN' ? 'primary' : 'secondary',
        onClick: handleCreateBusinessCase,
        isDisabled:
          isNilOrEmpty(customer) ||
          isActionsDisabled ||
          isCreateBusinessCaseDisabled({
            type: interest?.type,
            miniSale,
            offeredVehiclesList,
          }) ||
          !hasConvertToBusinessCaseInterestPermission,
        tooltip: !hasConvertToBusinessCaseInterestPermission
          ? i18n.t('general.labels.noPermission')
          : undefined,
        isLoading: isCloneFlowEnabled ? undefined : isConvertAllVehiclesLoading,
        leftIcon: 'places/business_center',
        title: i18n.t('entity.interest.actions.createBusinessCase'),
        'data-testid': testIds.interest.detail('headerActions-createBusinessCase'),
      }
    )
    .when(interest?.state === 'NEW', {
      type: 'button',
      variant: 'primary',
      onClick: () =>
        openInterest({
          id: interestId,
        })
          .unwrap()
          .catch(handleApiError),
      isLoading: isOpenInterestLoading,
      title: i18n.t('general.actions.open'),
      isDisabled: !hasOpenInterestPermission,
      tooltip: !hasOpenInterestPermission ? i18n.t('general.labels.noPermission') : undefined,
      'data-testid': testIds.interest.detail('headerActions-open'),
    })
    .when(interest?.state === 'UNSUCCESSFUL', {
      type: 'button',
      variant: 'secondary',
      onClick: handleReopen,
      isLoading: isReopenInterestLoading,
      title: i18n.t('general.actions.reopen'),
      isDisabled: !hasReopenInterestPermission,
      tooltip: !hasReopenInterestPermission ? i18n.t('general.labels.noPermission') : undefined,
      'data-testid': testIds.interest.detail('headerActions-reopen'),
    });
}
